// Force frontend rebuild - direct access working
import { useEffect, Suspense, lazy } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { theme } from './theme';
import { AuthProvider } from './contexts/AuthContext';
import { SudoProvider } from './contexts/SudoContext';
import PrivateRoute from './components/core/PrivateRoute';
import Layout from './components/core/Layout';
import { LoadingSpinner } from './components/shared/LoadingSpinner';
import AdminGuard from './components/admin/AdminGuard';
import ModuleGuard from './components/core/ModuleGuard';

// Regular imports for login/registration which are not behind the protected layout
import LoginPage from './pages/LoginPage';
import RegistrationPage from './pages/RegistrationPage';

// Lazy-loaded page components for code-splitting
const SynapseConsolePage = lazy(() => import('./pages/SynapseConsolePage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const SalesSimulatorPage = lazy(() => import('./pages/SalesSimulatorPage'));
const PartnerRecruitmentSimulatorPage = lazy(() => import('./pages/PartnerRecruitmentSimulatorPage'));
const PartnerGrowthPlanningSimulatorPage = lazy(() => import('./pages/PartnerGrowthPlanningSimulatorPage'));
const SalesSimulatorHubPage = lazy(() => import('./pages/SalesSimulatorHubPage'));
const SynapseSolutioningPage = lazy(() => import('./pages/SynapseSolutioningPage'));
const PartnerGrowthPlannerPage = lazy(() => import('./pages/PartnerGrowthPlannerPage'));
const GrowthPlanViewPage = lazy(() => import('./pages/advisor/GrowthPlanViewPage'));
const SolutionViewPage = lazy(() => import('./pages/advisor/SolutionViewPage'));
const StrategicAccountPlannerPage = lazy(() => import('./pages/StrategicAccountPlannerPage'));
const ContentWorkshopPage = lazy(() => import('./pages/studio/ContentWorkshopPage'));
const GlobalVoiceDashboard = lazy(() => import('./pages/studio/global-voice/GlobalVoiceDashboard'));
const GlobalVoiceNewTranslation = lazy(() => import('./pages/studio/global-voice/GlobalVoiceNewTranslation'));
const GlobalVoiceProjects = lazy(() => import('./pages/studio/global-voice/GlobalVoiceProjects'));
const GlobalVoiceGlossary = lazy(() => import('./pages/studio/global-voice/GlobalVoiceGlossary'));
const GlobalVoiceSupport = lazy(() => import('./pages/studio/global-voice/GlobalVoiceSupport'));
const GlobalVoiceReview = lazy(() => import('./pages/studio/global-voice/GlobalVoiceReview'));
const SynapseImagenPage = lazy(() => import('./pages/SynapseImagenPage'));
const SynapseAcademyPage = lazy(() => import('./pages/SynapseAcademyPage'));
const SynapseAdvisorPage = lazy(() => import('./pages/SynapseAdvisorPage'));
const SynapseStudioPage = lazy(() => import('./pages/SynapseStudioPage'));
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'));
const SynapseAssistantPage = lazy(() => import('./pages/SynapseAssistantPage'));
const CompetencyProfilerPage = lazy(() => import('./pages/CompetencyProfilerPage'));
const KickstartHubPage = lazy(() => import('./pages/KickstartHubPage'));
const KickstartModulePage = lazy(() => import('./pages/KickstartModulePage'));
const DeepDiveHubPage = lazy(() => import('./pages/skills/DeepDiveHubPage'));
const DeepDiveCoursePage = lazy(() => import('./pages/skills/DeepDiveCoursePage'));
const AiSettingsPage = lazy(() => import('./pages/admin/AiSettingsPage'));
const MaintenancePage = lazy(() => import('./pages/admin/MaintenancePage'));
const AILogsPage = lazy(() => import('./pages/admin/AILogsPage'));
const SynapseLibraryPage = lazy(() => import('./pages/SynapseLibraryPage'));
const UserManagement = lazy(() => import('./components/admin/UserManagement'));
const CompanyManagement = lazy(() => import('./components/admin/CompanyManagement'));
const BulkKnowledgeBaseUploadsPage = lazy(() => import('./pages/admin/BulkKnowledgeBaseUploadsPage'));
const ContentManagementPage = lazy(() => import('./pages/admin/ContentManagementPage'));
const ScenarioManagementPage = lazy(() => import('./pages/admin/ScenarioManagementPage'));
const SynapseResearcherPage = lazy(() => import('./pages/advisor/SynapseResearcherPage'));
const PartnerIntelligencePage = lazy(() => import('./pages/advisor/PartnerIntelligencePage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const VendorHubPage = lazy(() => import('./pages/VendorHubPage'));
const GoogleDealAnalyzerPage = lazy(() => import('./pages/vendorhub/GoogleDealAnalyzerPage'));
const MicrosoftEnablementPage = lazy(() => import('./pages/vendorhub/MicrosoftEnablementPage'));
const MicrosoftLicensingCoachPage = lazy(() => import('./pages/vendorhub/MicrosoftLicensingCoachPage'));
const NorthAmericaSolutionBuilderPage = lazy(() => import('./pages/advisor/NorthAmericaSolutionBuilderPage'));
const CourseManagementPage = lazy(() => import('./pages/studio/CourseManagementPage'));
const CourseEditorPage = lazy(() => import('./pages/studio/CourseEditorPage'));
const CourseReviewPage = lazy(() => import('./pages/studio/CourseReviewPage'));
const CourseDispatchPage = lazy(() => import('./pages/skills/CourseDispatchPage'));
const SynapseNewsArchivePage = lazy(() => import('./pages/news/SynapseNewsArchivePage'));
const SynapseNewsArticlePage = lazy(() => import('./pages/news/SynapseNewsArticlePage'));
const NewsManagementPage = lazy(() => import('./pages/studio/NewsManagementPage'));
const NewsEditorPage = lazy(() => import('./pages/studio/NewsEditorPage'));

const getTitle = (projectId: string) => {
  switch (projectId) {
    case 'td-synapse-dev':
      return 'DEV-SYNAPSE';
    case 'td-synapse-test':
      return 'TEST-SYNAPSE';
    case 'td-synapse-prod':
      return 'TD SYNNEX Synapse';
    default:
      return 'TD SYNNEX Synapse';
  }
};

// This component wraps protected routes with the PrivateRoute and Layout,
// allowing the content of the route to be loaded lazily via the <Outlet />.
const ProtectedLayout = () => (
  <PrivateRoute>
    <Layout>
      <Outlet />
    </Layout>
  </PrivateRoute>
);

export default function App() {
  useEffect(() => {
    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || '';
    document.title = getTitle(projectId);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <SudoProvider>
          <Router>
            {/* Suspense provides a fallback while lazy-loaded components are fetched */}
            <Suspense fallback={<LoadingSpinner message="Loading Page..." />}>
              <Routes>
                {/* Public routes load immediately */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegistrationPage />} />
                <Route path="/course/dispatch/:courseId" element={<CourseDispatchPage />} />

                {/* All protected routes are nested under the ProtectedLayout */}
                <Route element={<ProtectedLayout />}>
                  <Route path="/dashboard" element={<SynapseConsolePage />} />
                  <Route path="/news" element={<SynapseNewsArchivePage />} />
                  <Route path="/news/:slug" element={<SynapseNewsArticlePage />} />
                  
                  {/* Redirects for old routes */}
                  <Route path="/console" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/assistant" element={<Navigate to="/assist" replace />} />
                  <Route path="/academy/course-creation/*" element={<Navigate to="/studio/course-management" replace />} />
                  <Route path="/academy/deep-dive/*" element={<Navigate to="/skills/deep-dive" replace />} />
                  <Route path="/academy/*" element={<Navigate to="/skills" replace />} />
                  <Route path="/assistant/*" element={<Navigate to="/assist" replace />} />
                  
                  {/* Academy Module -> Skills Module */}
                  <Route element={<ModuleGuard requiredModule="skills" />}>
                    <Route path="/skills" element={<SynapseAcademyPage />} />
                    <Route path="/skills/sales-simulator" element={<SalesSimulatorHubPage />} />
                    <Route path="/skills/sales-simulator/solution-selling" element={<SalesSimulatorPage />} />
                    <Route path="/skills/sales-simulator/partner-recruitment" element={<PartnerRecruitmentSimulatorPage />} />
                    <Route path="/skills/sales-simulator/partner-growth-planning" element={<PartnerGrowthPlanningSimulatorPage />} />
                    <Route path="/skills/competency-profiler" element={<CompetencyProfilerPage />} />
                    <Route path="/skills/kickstart" element={<KickstartHubPage />} />
                    <Route path="/skills/kickstart/:moduleId" element={<KickstartModulePage />} />
                    
                    <Route element={<ModuleGuard requiredModule="skills" requiredSubModule="deep-dive" />}>
                      <Route path="/skills/deep-dive" element={<DeepDiveHubPage />} />
                      <Route path="/skills/deep-dive/course/:courseSlug" element={<DeepDiveCoursePage />} />
                    </Route>
                  </Route>

                  {/* Advisor Module */}
                  <Route element={<ModuleGuard requiredModule="advisor" />}>
                    <Route path="/advisor" element={<SynapseAdvisorPage />} />
                    <Route path="/advisor/partner-growth-planner" element={<PartnerGrowthPlannerPage />} />
                    <Route path="/advisor/partner-growth-planner/plans" element={<Navigate to="/library?tab=growth-plans" replace />} />
                    <Route path="/advisor/partner-growth-planner/plans/:planId" element={<GrowthPlanViewPage />} />
                    {/* Sub-module protection for Strategic Account Planner */}
                    <Route element={<ModuleGuard requiredModule="advisor" requiredSubModule="strategic-account-planner" />}>
                        <Route path="/advisor/strategic-account-planner" element={<StrategicAccountPlannerPage />} />
                    </Route>
                    <Route path="/advisor/synapse-solutioning" element={<SynapseSolutioningPage />} />
                    <Route path="/advisor/synapse-solutioning/solutions" element={<Navigate to="/library?tab=solutions" replace />} />
                    <Route path="/advisor/synapse-solutioning/solutions/:solutionId" element={<SolutionViewPage />} />
                    <Route element={<ModuleGuard requiredModule="advisor" requiredSubModule="na-solution-builder" />}>
                        <Route path="/advisor/na-solution-builder" element={<NorthAmericaSolutionBuilderPage />} />
                    </Route>
                    <Route path="/advisor/synapse-researcher" element={<SynapseResearcherPage />} />
                    <Route path="/advisor/partner-intelligence" element={<PartnerIntelligencePage />} />
                  </Route>

                  {/* Studio Module */}
                  <Route element={<ModuleGuard requiredModule="studio" />}>
                    <Route path="/studio" element={<SynapseStudioPage />} />
                    
                    <Route element={<ModuleGuard requiredModule="studio" requiredSubModule="content-workshop" />}>
                      <Route path="/studio/content-workshop" element={<ContentWorkshopPage />} />
                    </Route>
                    
                    <Route element={<ModuleGuard requiredModule="studio" requiredSubModule="global-voice" />}>
                      <Route path="/studio/global-voice" element={<GlobalVoiceDashboard />} />
                      <Route path="/studio/global-voice/new" element={<GlobalVoiceNewTranslation />} />
                      <Route path="/studio/global-voice/projects" element={<GlobalVoiceProjects />} />
                      <Route path="/studio/global-voice/review/:projectId" element={<GlobalVoiceReview />} />
                      <Route path="/studio/global-voice/glossary" element={<GlobalVoiceGlossary />} />
                      <Route path="/studio/global-voice/support" element={<GlobalVoiceSupport />} />
                    </Route>
                    
                    <Route element={<ModuleGuard requiredModule="studio" requiredSubModule="synapse-imagen" />}>
                      <Route path="/studio/synapse-imagen" element={<SynapseImagenPage />} />
                    </Route>
                    
                    <Route element={<ModuleGuard requiredModule="studio" requiredSubModule="course-management" />}>
                      <Route path="/studio/course-management" element={<CourseManagementPage />} />
                      <Route path="/studio/course-management/new" element={<CourseEditorPage />} />
                      <Route path="/studio/course-management/edit/:courseId" element={<CourseEditorPage />} />
                      <Route path="/studio/course-management/review/:courseId" element={<CourseReviewPage />} />
                    </Route>

                    <Route element={<ModuleGuard requiredModule="studio" requiredSubModule="news-management" />}>
                      <Route path="/studio/news-management" element={<NewsManagementPage />} />
                      <Route path="/studio/news-management/new" element={<NewsEditorPage />} />
                      <Route path="/studio/news-management/edit/:articleId" element={<NewsEditorPage />} />
                    </Route>
                  </Route>

                  {/* Library Module */}
                  <Route element={<ModuleGuard requiredModule="library" />}>
                    <Route path="/library" element={<SynapseLibraryPage />} />
                  </Route>

                  {/* Assistant Module -> Assist Module */}
                  <Route element={<ModuleGuard requiredModule="assist" />}>
                    <Route path="/assist" element={<SynapseAssistantPage />} />
                  </Route>
                  
                  {/* Vendor Hub Module */}
                  <Route element={<ModuleGuard requiredModule="vendor-hub" />}>
                    <Route path="/vendor-hub">
                        <Route index element={<VendorHubPage />} />
                        <Route path=":vendorId" element={<VendorHubPage />} />
                        <Route path="google/deal-analyzer" element={<GoogleDealAnalyzerPage />} />
                        <Route path="microsoft/enablement" element={<MicrosoftEnablementPage />} />
                        <Route path="microsoft/licensing-coach" element={<MicrosoftLicensingCoachPage />} />
                    </Route>
                  </Route>

                  {/* Admin Routes - Protected by AdminGuard (Sudo Mode + Role Check) */}
                  <Route element={<AdminGuard />}>
                    <Route path="/admin" element={<AdminPage />} />
                    <Route path="/admin/configuration" element={<Navigate to="/admin/prompt-configurations" replace />} />
                    <Route path="/admin/prompt-configurations" element={<AiSettingsPage />} />
                    <Route path="/admin/user-management" element={<UserManagement />} />
                    <Route path="/admin/company-management" element={<CompanyManagement />} />
                    <Route path="/admin/content-management" element={<ContentManagementPage />} />
                    <Route path="/admin/academy-scenarios" element={<ScenarioManagementPage />} />
                    <Route path="/admin/content-curation" element={<Navigate to="/studio/library" />} />
                    <Route path="/admin/maintenance" element={<MaintenancePage />} />
                    <Route path="/admin/bulk-uploads" element={<BulkKnowledgeBaseUploadsPage />} />
                    <Route path="/admin/ai-logs" element={<AILogsPage />} />
                  </Route>

                  <Route path="/profile" element={<UserProfilePage />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                  
                  <Route path="/" element={<Navigate to="/dashboard" />} />
                </Route>
                
                {/* A final fallback to redirect any unknown paths to the console */}
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </Suspense>
          </Router>
        </SudoProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
